import React, { useState } from "react";
import { BrowserRouter, NavLink, Navigate, Outlet, Route, Routes } from "react-router-dom";
import "./App.css";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import ShopSwitcher from "./components/ShopSwitcher";
import LoginPage from "./pages/LoginPage";
import InventoryPage from "./pages/InventoryPage";
import SalesPage from "./pages/SalesPage";
import ExpensesPage from "./pages/ExpensesPage";
import ReportsPage from "./pages/ReportsPage";
import OrganizationsPage from "./pages/OrganizationsPage";
import ShopsUsersPage from "./pages/ShopsUsersPage";
import AccountPage from "./pages/AccountPage";

const roleLabel = (role) => (role === "org_admin" ? "Org Admin" : role ? role.charAt(0).toUpperCase() + role.slice(1) : "");

function AppLayout() {
  const { user, activeShop, logout } = useAuth();
  // Cashiers have no expense/report permissions at all (not even read), so
  // don't even show them links to pages that will just 403.
  const canSeeFinancials = activeShop?.role !== "cashier";
  const isOrgAdmin = activeShop?.role === "org_admin";
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);
  const navLinkClass = ({ isActive }) => (isActive ? "nav-link active" : "nav-link");

  return (
    <div className="app-bg">
      <header className="topbar">
        <div className="brand">
          <span className="brand-badge">📦</span>
          <h1>Inventory</h1>
        </div>
        <button
          type="button"
          className="menu-toggle"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {menuOpen ? "✕" : "☰"}
        </button>
        <div className={menuOpen ? "topbar-collapse open" : "topbar-collapse"}>
          <nav className="main-nav">
            <NavLink to="/" end className={navLinkClass} onClick={closeMenu}>
              Inventory
            </NavLink>
            <NavLink to="/sales" className={navLinkClass} onClick={closeMenu}>
              Sales
            </NavLink>
            {canSeeFinancials && (
              <NavLink to="/expenses" className={navLinkClass} onClick={closeMenu}>
                Expenses
              </NavLink>
            )}
            {canSeeFinancials && (
              <NavLink to="/reports" className={navLinkClass} onClick={closeMenu}>
                Reports
              </NavLink>
            )}
            {isOrgAdmin && (
              <NavLink to="/team" className={navLinkClass} onClick={closeMenu}>
                Shops & Users
              </NavLink>
            )}
            {user?.is_product_owner && (
              <NavLink to="/organizations" className={navLinkClass} onClick={closeMenu}>
                Organizations
              </NavLink>
            )}
          </nav>
          <div className="top-actions">
            <ShopSwitcher />
            {user && (
              <NavLink to="/account" className="chip user-chip" onClick={closeMenu}>
                {user.full_name || user.email}
                {activeShop?.role && ` · ${roleLabel(activeShop.role)}`}
              </NavLink>
            )}
            <button
              className="btn btn-light"
              onClick={() => {
                closeMenu();
                logout();
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container">
        <Outlet />
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<InventoryPage />} />
            <Route path="sales" element={<SalesPage />} />
            <Route path="expenses" element={<ExpensesPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="team" element={<ShopsUsersPage />} />
            <Route path="account" element={<AccountPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
